import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { slugify } from '../shared/testid/testid.util';

// Reusable service + specialty selection picker shared by the vendor Service
// Capabilities section and the Coverage Zones Add/Edit form. It renders the
// services it is given as a responsive grid of cards, each with a header
// include-checkbox (bound to `service.enabledForZone`) and — when the service is
// on — its specialties as toggle-chips (bound to each sub-type's
// `enabledForZone`). The picker owns only the selection UI + interaction rules;
// it never talks to userService/user. The host binds a services array, reacts to
// `selectionChange`, and persists.
//
// Selection model (mutated in place on the bound objects, then emitted):
//  - `service.enabledForZone`  — whether the service is included in the zone.
//  - `subType.enabledForZone`  — whether that specialty is selected.
//
// Locked interaction rules (mirroring OT + the current manage-account handlers):
//  - Chip click toggles that sub-type's `enabledForZone`.
//  - Deselecting every specialty KEEPS the service on (never auto-turns-off).
//  - The header checkbox toggles the whole service; unchecking hides the
//    specialties but LEAVES the sub-type flags dormant (see the note on
//    `onServiceToggle`).
//  - The tri-state Select-all cascades on/off to every service + sub-type.
@Component({
  standalone: true,
  selector: 'service-capability-picker',
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './service-capability-picker.component.html',
  styleUrl: './service-capability-picker.component.scss',
})
export class ServiceCapabilityPickerComponent implements OnInit, OnChanges {
  // The services to render. Each carries `displayName`, `type`, `enabledForZone`
  // and an optional `serviceSubTypes[]`, where each sub-type carries
  // `displayName`, `type`, `enabledForZone`. Mutated in place as the vendor
  // selects; the host reads the same objects back via `selectionChange`.
  @Input() services: any[] = [];

  // Add-a-Zone case: when true, every service + sub-type is initialized on the
  // moment a services array arrives (all offered capabilities pre-checked).
  @Input() preselectAll = false;

  // Whether the tri-state Select-all header control is rendered. Defaults to
  // true so the zone Add/Edit form (local selection, single save) is
  // unaffected. Section 1 "Service Capabilities" sets this false: it flips
  // the vendor's account-level capability set one item at a time (no bulk
  // endpoint), so a single Select-all click would fire a burst of per-item
  // API calls. The Select-all LOGIC (allSelected/toggleSelectAll/etc.) is
  // untouched either way — only the control's visibility is gated.
  @Input() showSelectAll = true;

  // Emitted after every selection change (and after an initial preselect-all) so
  // the host always holds the latest selection without reaching into the picker.
  @Output() selectionChange = new EventEmitter<any[]>();

  // Guards the Add-a-Zone preselect so the cascade runs exactly ONCE — the first
  // time a services array is present. Without this, a later `services` reference
  // change (e.g. Section 1 persisting a capability reassigns the host's bound
  // array while the Add form is open) would re-cascade and silently reset the
  // picker to "all selected", discarding the vendor's in-progress deselections.
  private hasPreselected = false;

  ngOnInit(): void {
    this.maybePreselectAll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only the FIRST services array triggers the preselect (see maybePreselectAll).
    // A subsequent reference change must NOT re-cascade — doing so would wipe the
    // user's deselections. The Edit picker (preselectAll=false) never cascades
    // here and is unaffected either way.
    if (changes['services']) {
      this.maybePreselectAll();
    }
  }

  // Applies the preselect-all cascade once — the first time a non-empty services
  // array arrives (Add-a-Zone only). Idempotent on every later invocation.
  private maybePreselectAll(): void {
    if (
      this.preselectAll &&
      !this.hasPreselected &&
      (this.services?.length ?? 0) > 0
    ) {
      this.hasPreselected = true;
      this.cascadeAll(true);
      this.emit();
    }
  }

  // Stable testid parts, keyed on each item's `type` (not the user-visible
  // `displayName`, which can change copy without changing identity).
  serviceTestKey(service: any): string {
    return slugify(service?.type || service?.displayName || 'service');
  }
  subTypeTestKey(subType: any): string {
    return slugify(subType?.type || subType?.displayName || 'specialty');
  }

  // Total number of specialties offered under a service.
  subTypeCount(service: any): number {
    return service?.serviceSubTypes?.length ?? 0;
  }

  // How many of a service's specialties are currently selected — the "N" in the
  // informational N/M count.
  selectedSubTypeCount(service: any): number {
    return (service?.serviceSubTypes ?? []).filter(
      (st: any) => st?.enabledForZone,
    ).length;
  }

  // Toggle one specialty. Deselecting the last one is allowed and does NOT turn
  // the parent service off — the service stays included with zero specialties.
  toggleSubType(service: any, subType: any): void {
    if (!service?.enabledForZone) {
      return;
    }
    subType.enabledForZone = !subType.enabledForZone;
    this.emit();
  }

  // Header include-checkbox change. `[(ngModel)]` has already flipped
  // `service.enabledForZone` by the time this fires. On uncheck we deliberately
  // LEAVE the sub-type `enabledForZone` flags as they are (dormant) rather than
  // clearing them: the current manage-account save persists the full services
  // array and downstream consumers read specialties only through
  // `filterServiceList` (enabled services only), so a disabled service's dormant
  // sub-type flags are inert. This also matches the existing new/edit-zone grids,
  // which hide the specialties on uncheck without resetting their flags.
  onServiceToggle(_service: any): void {
    this.emit();
  }

  // Tri-state Select-all: everything on only when every service AND every
  // sub-type is selected.
  get allSelected(): boolean {
    const services = this.services ?? [];
    if (services.length === 0) {
      return false;
    }
    return services.every(
      (s: any) =>
        s?.enabledForZone &&
        (s?.serviceSubTypes ?? []).every((st: any) => st?.enabledForZone),
    );
  }

  // Nothing on when no service is included and no sub-type is selected.
  get noneSelected(): boolean {
    const services = this.services ?? [];
    return services.every(
      (s: any) =>
        !s?.enabledForZone &&
        (s?.serviceSubTypes ?? []).every((st: any) => !st?.enabledForZone),
    );
  }

  // The Select-all checkbox is indeterminate whenever the selection is mixed.
  get selectAllIndeterminate(): boolean {
    return !this.allSelected && !this.noneSelected;
  }

  // Clicking Select-all turns everything on unless it is already fully on, in
  // which case it turns everything off (standard select-all toggle).
  toggleSelectAll(): void {
    this.cascadeAll(!this.allSelected);
    this.emit();
  }

  // Cascade a target enabled state to every service and every sub-type.
  private cascadeAll(target: boolean): void {
    for (const service of this.services ?? []) {
      service.enabledForZone = target;
      for (const subType of service.serviceSubTypes ?? []) {
        subType.enabledForZone = target;
      }
    }
  }

  private emit(): void {
    this.selectionChange.emit(this.services);
  }
}
